import clsx from 'clsx'
import { FocusEvent, ForwardedRef, forwardRef, InputHTMLAttributes, ReactElement, RefObject, useState } from 'react'

import { FormInputProps } from '../fields/types'
import { useFocusOnError } from './use-focus-on-error'

export type TextAreaInputProps = Omit<InputHTMLAttributes<HTMLTextAreaElement>, 'form' | 'onChange'> &
  Omit<FormInputProps, 'onBlur'> & {
    onChange?: (v: string) => void
  }

const TextAreaInput = (
  {
    name,
    className,
    label,
    description,
    disabled,
    placeholder,
    useNestedLabel,
    value,
    hasErrors,
    onChange,
    onBlur,
    onFocus,
    focusOnError,
    ...rest
  }: TextAreaInputProps,
  ref: ForwardedRef<HTMLTextAreaElement>,
) => {
  const [isFocused, setIsFocused] = useState(false)
  useFocusOnError(ref as RefObject<HTMLTextAreaElement>, hasErrors, focusOnError)

  const inputClasses = clsx(
    className,
    'placeholder:text-light flex h-12 h-24 min-h-12 w-full flex-1 flex-col rounded-md px-4 py-3 shadow outline-0',
    !disabled && 'text-dark bg-white',
    disabled && 'text-light cursor-not-allowed bg-slate-100',
    !isFocused && !hasErrors && 'ring ring-1 ring-slate-300',
    !isFocused && hasErrors && 'z-2 ring-2 ring-red-500',
    isFocused && 'ring-brand z-2 ring-2',
    useNestedLabel && value && 'pt-5',
  )

  return (
    <div className='flex w-full min-w-0'>
      <textarea
        {...rest}
        id={name}
        name={name}
        aria-describedby={`${name}-description`}
        className={clsx(inputClasses, 'w-full')}
        disabled={disabled}
        placeholder={useNestedLabel ? (label as string) : placeholder}
        onFocus={(e: FocusEvent<HTMLTextAreaElement, Element>) => {
          setIsFocused(true)
          if (onFocus) onFocus(e)
        }}
        onBlur={(e: FocusEvent<HTMLTextAreaElement, Element>) => {
          setIsFocused(false)
          if (onBlur) onBlur(e)
        }}
        onChange={(e) => {
          if (onChange) onChange(e.target.value)
        }}
        value={value}
        ref={ref}
      />
    </div>
  )
}

export default forwardRef<HTMLTextAreaElement, TextAreaInputProps>(TextAreaInput)
